import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SdiLandingPage } from '../../patient-files/actions/sdi/SdiLandingPage';
import { advanceWorkflow } from '../../../services/workQueue';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useToast } from '../../../providers/ToastProvider';
import { invalidateCaseDetail } from '../util';
import type { TaskBodyProps } from './index';
import { findOdoForwardAction } from '../odoForwardAction';

export function CreateSdiTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    // Orthopulse status drives the SDI cadence + PDF; thread it through the same
    // way the Actions tab does so a work-queue-launched SDI isn't stuck on the
    // default cadence.
    const { data: card } = usePatientCard(c.id);
    const orthopulseStatus = card?.orthopulse?.status ?? null;

    const forwardAction = findOdoForwardAction(statusMeta.outgoingActions);

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmitted = useCallback(async () => {
        try {
            await advanceWorkflow(c.id, 'create-sdi', {});
            onTaskComplete?.(forwardAction);
        } catch {
            void invalidateCaseDetail(queryClient, c.id);
            showToast('Failed to advance case after SDI submission. Please try again.', 'error');
        }
    }, [c.id, forwardAction, onTaskComplete, showToast, queryClient]);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <SdiLandingPage
                patientId={c.id}
                patientName={c.name}
                orthopulseStatus={orthopulseStatus}
                onBack={() => {
                    /* no-op — TaskSurface owns the back button */
                }}
                onSubmitted={handleSubmitted}
                inWorkflow
            />
        </div>
    );
}
